import { limitar, vetor, type Vetor3 } from '../dominio/geometria/vetor3'

/**
 * Trajetória resolvida de um corpo, como quadros-chave.
 *
 * A engine de física resolve o movimento UMA vez, fora do laço de desenho, e o
 * resultado é guardado aqui. Depois a cena só amostra esta tabela — como já faz
 * com os quadros escritos à mão de `timelineCinematica.ts`.
 *
 * É o que conciliou física real com a régua de tempo da aula:
 *
 * - o arrastão da régua de tempo funciona nos dois sentidos, e física não roda
 *   para trás;
 * - o resultado é o mesmo a cada execução, porque é calculado uma vez e relido,
 *   não reintegrado a cada quadro com o delta do relógio de parede;
 * - nenhum quadro de desenho paga custo de simulação.
 *
 * Módulo puro: sem React, sem Three.js e sem Rapier. Quem fala com a engine é
 * `solucionador.ts`.
 */

/** Quaternion de rotação, na ordem x, y, z, w. */
export interface Quaternion {
  x: number
  y: number
  z: number
  w: number
}

export const QUATERNION_IDENTIDADE: Quaternion = { x: 0, y: 0, z: 0, w: 1 }

export interface QuadroCorpo {
  posicao: Vetor3
  rotacao: Quaternion
}

export interface TrajetoriaCorpo {
  /** id do ocupante ou do segmento físico a que este corpo corresponde */
  id: string
  /** um quadro por passo de simulação, do instante 0 ao 1 */
  quadros: readonly QuadroCorpo[]
}

/** Segmentos articulados do ragdoll completo usado apenas na cinemática. */
export type SegmentoRagdoll =
  | 'pelve'
  | 'tronco'
  | 'cabeca'
  | 'bracoEsquerdo'
  | 'bracoDireito'
  | 'antebracoEsquerdo'
  | 'antebracoDireito'
  | 'coxaEsquerda'
  | 'coxaDireita'
  | 'pernaEsquerda'
  | 'pernaDireita'
  | 'peEsquerdo'
  | 'peDireito'

/**
 * A pelve preserva o id público do ocupante para compatibilidade. Os outros
 * segmentos ganham ids internos determinísticos e nunca viram novos ocupantes.
 */
export const idSegmentoRagdoll = (idOcupante: string, segmento: SegmentoRagdoll): string =>
  segmento === 'pelve' ? idOcupante : `${idOcupante}:${segmento}`

/**
 * Interpolação de rotação por lerp normalizado, não slerp.
 *
 * Com centenas de passos a rotação entre dois quadros consecutivos é pequena, e
 * nessa faixa o lerp normalizado é indistinguível do slerp — enquanto o slerp
 * exigiria tratar o caso dos quaterniões quase opostos. O sinal é corrigido
 * antes de interpolar para tomar sempre o caminho curto.
 */
export function misturarQuaternion(a: Quaternion, b: Quaternion, t: number): Quaternion {
  const sinal = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w < 0 ? -1 : 1
  const x = a.x + (b.x * sinal - a.x) * t
  const y = a.y + (b.y * sinal - a.y) * t
  const z = a.z + (b.z * sinal - a.z) * t
  const w = a.w + (b.w * sinal - a.w) * t
  const norma = Math.hypot(x, y, z, w)
  if (norma < 1e-9) return { ...a }
  return { x: x / norma, y: y / norma, z: z / norma, w: w / norma }
}

/**
 * Estado do corpo no instante normalizado, interpolado entre os quadros.
 *
 * O tempo é o MESMO 0..1 da timeline didática: a tabela tem um quadro por passo
 * de simulação, distribuídos uniformemente nesse intervalo.
 */
export function amostrarTrajetoria(trajetoria: TrajetoriaCorpo, tempo: number): QuadroCorpo {
  const quadros = trajetoria.quadros
  if (quadros.length === 0) {
    return { posicao: vetor(0, 0, 0), rotacao: QUATERNION_IDENTIDADE }
  }
  if (quadros.length === 1) return quadros[0]!

  const t = limitar(tempo, 0, 1)
  const posicaoExata = t * (quadros.length - 1)
  const indice = Math.min(Math.floor(posicaoExata), quadros.length - 2)
  const fracao = posicaoExata - indice
  const a = quadros[indice]!
  const b = quadros[indice + 1]!

  return {
    posicao: vetor(
      a.posicao.x + (b.posicao.x - a.posicao.x) * fracao,
      a.posicao.y + (b.posicao.y - a.posicao.y) * fracao,
      a.posicao.z + (b.posicao.z - a.posicao.z) * fracao,
    ),
    rotacao: misturarQuaternion(a.rotacao, b.rotacao, fracao),
  }
}

export function trajetoriaPorId(
  trajetorias: readonly TrajetoriaCorpo[],
  id: string,
): TrajetoriaCorpo | null {
  return trajetorias.find((t) => t.id === id) ?? null
}
